import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/features/auth';
import { fluxoCaixaService } from '../services/fluxoCaixaService';
import {
  FluxoCaixaFiltros,
  FluxoCaixaFormData,
  FluxoCaixaLancamento,
  FluxoCaixaPeriodo,
  FluxoCaixaResumo,
  getDefaultFiltrosTabela,
  getDefaultPeriodoResumo,
} from '../types/fluxoCaixa';

export function useFluxoCaixa() {
  const { profile } = useAuth();
  const tenantId = profile?.tenant_id ? Number(profile.tenant_id) : undefined;
  const userId = profile?.uuid;

  const [periodoResumo, setPeriodoResumo] = useState<FluxoCaixaPeriodo>(getDefaultPeriodoResumo);
  const [filtros, setFiltros] = useState<FluxoCaixaFiltros>(getDefaultFiltrosTabela);
  const [lancamentos, setLancamentos] = useState<FluxoCaixaLancamento[]>([]);
  const [resumo, setResumo] = useState<FluxoCaixaResumo>({
    total_entrada: 0,
    total_saida: 0,
    saldo: 0,
  });
  const [loadingTabela, setLoadingTabela] = useState(true);
  const [loadingResumo, setLoadingResumo] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const carregarTabela = useCallback(async () => {
    if (!tenantId) return;

    try {
      setLoadingTabela(true);
      const lista = await fluxoCaixaService.listar(tenantId, filtros);
      setLancamentos(lista);
      setError(null);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Erro ao carregar lançamentos');
      setError(e);
      toast.error(e.message);
    } finally {
      setLoadingTabela(false);
    }
  }, [tenantId, filtros]);

  const carregarResumo = useCallback(async () => {
    if (!tenantId) return;

    try {
      setLoadingResumo(true);
      const resumoData = await fluxoCaixaService.obterResumo(
        tenantId,
        periodoResumo.dataInicio,
        periodoResumo.dataFim
      );
      setResumo(resumoData);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Erro ao carregar resumo');
      toast.error(e.message);
    } finally {
      setLoadingResumo(false);
    }
  }, [tenantId, periodoResumo]);

  const recarregar = useCallback(async () => {
    await Promise.all([carregarTabela(), carregarResumo()]);
  }, [carregarTabela, carregarResumo]);

  useEffect(() => {
    carregarTabela();
  }, [carregarTabela]);

  useEffect(() => {
    carregarResumo();
  }, [carregarResumo]);

  const criar = async (formData: FluxoCaixaFormData) => {
    if (!tenantId || !userId) return;

    try {
      await fluxoCaixaService.criar(tenantId, userId, formData);
      toast.success('Lançamento criado com sucesso');
      await recarregar();
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Erro ao criar lançamento');
      toast.error(e.message);
      throw e;
    }
  };

  const atualizar = async (id: number, formData: Partial<FluxoCaixaFormData>) => {
    try {
      await fluxoCaixaService.atualizar(id, formData);
      toast.success('Lançamento atualizado com sucesso');
      await recarregar();
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Erro ao atualizar lançamento');
      toast.error(e.message);
      throw e;
    }
  };

  const deletar = async (id: number) => {
    try {
      await fluxoCaixaService.deletar(id);
      toast.success('Lançamento removido com sucesso');
      await recarregar();
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Erro ao remover lançamento');
      toast.error(e.message);
      throw e;
    }
  };

  return {
    lancamentos,
    resumo,
    periodoResumo,
    setPeriodoResumo,
    filtros,
    setFiltros,
    loadingTabela,
    loadingResumo,
    error,
    criar,
    atualizar,
    deletar,
    recarregar,
  };
}
